import { log } from '@utils/debug'
import React from 'react'
import { useErrorBoundary } from 'react-error-boundary'
import { generateEndpointUrl, generateQueryParams } from '@utils/validation-net'
import { useAtom } from 'jotai'
import {
	silentFetches,
	//sessioneAtom,
	mobileTokenAtom,
	currentStepAtom,
} from '@store/store.ts'
import { prepareEncryptedHeader } from '@utils/crypto-utils'
import { generateHmacSHA256 } from '@utils/hmac-utils'
import { dataStateAtom } from '@store/atoms'
import type { APIRequest } from '@hooks/use-api-requests.hook'
import { useAppContext } from '@hooks/use-app-context'

type RequestHeaders = {
	'Content-Type':
		| 'application/json'
		| 'multipart/form-data'
		| 'application/x-www-form-urlencoded'
	Authorization: string
	'Id-Sessione'?: string
	'Id-Sessione-Mobile'?: string
	'X-Custom-Encrypted'?: string
	'X-HMAC'?: string
}

type FetchMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

type APITriggerArgs = {
	body?: Record<string, unknown> | FormData
	query?: Record<string, string>
}

export type APIRepsonse<T> = [
	data: T | undefined | null,
	trigger: (args: APITriggerArgs) => void,
	fetching: boolean,
]



export type APIParams<T> = {
	request: APIRequest
	method?: FetchMethod
	body?: Record<string, unknown>
	queryParams?: Record<string, string>
	silent?: boolean
	sleeper?: boolean
	file?: boolean
	default?: T | null
	shouldCache?: boolean
}

type AuthHeader = Partial<{
	'Id-Sessione': string
	'Id-Sessione-Mobile': string
}>

// Funzione fetchData identica alla tua
async function fetchData<T>(
	method: FetchMethod,
	url: string,
	file: boolean,
	signal: AbortSignal,
	auth: AuthHeader,
	body?: Record<string, unknown> | FormData,
	passwordForHeader?: string,
	valueForHeader?: string,
	hmacKey?: string
): Promise<T | undefined> {
	try {
		const encryptedHeader = await prepareEncryptedHeader(
			valueForHeader ?? '',
			passwordForHeader ?? ''
		)

		const authHeader = Object.keys(auth).reduce((ctx, current) => {
			const key = current as keyof typeof auth
			if (auth[key] !== undefined) {
				return {
					...ctx,
					[current]: auth[key],
				}
			}
			return ctx
		}, {})

		const headers: Partial<RequestHeaders> = file
			? authHeader
			: { ...authHeader, 'Content-Type': 'application/json' }

		headers['X-Custom-Encrypted'] = encryptedHeader
		if (body && hmacKey && !(body instanceof FormData)) {
			const bodyStr = JSON.stringify(body)
			headers['X-HMAC'] = await generateHmacSHA256(hmacKey, bodyStr)
		}

		const requestInit =
			method === 'GET'
				? { headers, signal }
				: {
						method,
						headers,
						body: file ? (body as FormData) : JSON.stringify(body),
						signal,
					}

		const response = await fetch(url, requestInit)
		const text = await response.text()

		if (!response.ok) {
			throw new Error(`${text}`)
		}

		try {
			return JSON.parse(text) as T
		} catch {
			if (import.meta.env.DEV) console.warn('Risposta non JSON:', text)
			return undefined
		}
	} catch (error) {
		if (error instanceof TypeError && error.message === 'Failed to fetch') {
			throw new Error(
				"Si è verificato un errore durante l'elaborazione della richiesta."
			)
		}
		if (error instanceof DOMException && error.name === 'AbortError') {
			throw new Error('La richiesta è stata annullata.')
		}

		if (error instanceof Error) {
			throw error
		}

		throw new Error(
			typeof error === 'string' ? error : JSON.stringify(error, null, 2)
		)
	}
}

const useApi = <T>(args: APIParams<T>): APIRepsonse<T> => {
	
	const [currentSilentFetches, setCurrentSilentFetches] = useAtom(silentFetches)
	//const [sessione] = useAtom(sessioneAtom)
	const [mobileToken] = useAtom(mobileTokenAtom)
	const [currentStep] = useAtom(currentStepAtom)
	const [dataState] = useAtom(dataStateAtom)

	const [data, setData] = React.useState<T | null>(args.default || null)
	const [body, setBody] = React.useState<
		Record<string, unknown> | FormData | undefined
	>(args.body)
	const [fetching, setFetching] = React.useState(false)
	const [sleeper, setSleeping] = React.useState(args.sleeper)
	const [force, setForce] = React.useState(false)

	const { showBoundary } = useErrorBoundary()
	const {
		request,
		method = 'GET',
		queryParams,
		silent = false,
		file = false,
		shouldCache = true,
	} = args

	const CACHE_KEY = `${request}${generateQueryParams(queryParams ?? {})}-step${currentStep}`
	const emptySession = 'SessioneVuota2025!'

	const passwordHeader = dataState.token?.sessione?.replace(/-/g, '1') ?? emptySession
	const valueHeader = dataState?.IdRichiesta?.toString() ?? ''
	const hmacKey = passwordHeader

	//const API_URL =import.meta.env.VITE_API_URL
	const API_URL = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL
	
	const [url, setUrl] = React.useState(
		generateEndpointUrl(API_URL, request, queryParams)
	)

	const trigger = ({ body, query }: APITriggerArgs) => {
		setSleeping(false)
		setForce(false)
		log('the following request has woken up: ', url, { method, body, query })
		setBody({})

		const cacheItem = sessionStorage.getItem(CACHE_KEY)

		if (cacheItem) {
			sessionStorage.removeItem(CACHE_KEY)
		}

		if (body !== undefined) {
			log('body', 'set')
			setBody(body)
		}

		/* 	if (body) {
			log('body', 'set')
			setBody(body)
		} */

		if (query) {
			log('query', 'set')
			setUrl(generateEndpointUrl(API_URL, request, query))
		}

		if (!query && body === undefined) {
			setForce(true)
		}

		/* if (!query && !body) {
			log('nothing', 'set')
			setForce(true)
		} */
	}

	/* const trigger = ({ body, query }: APITriggerArgs) => {
		setSleeping(false)
		setForce(false)
		setBody(body ?? {})
		if (query) setUrl(generateEndpointUrl(API_URL, request, query))
		else if (!body) setForce(true)
	} */

	const dispatchSource = (payload: 'ADD' | 'REMOVE', silentFetch: boolean) => {
		if (silentFetch) {
			setCurrentSilentFetches((current) =>
				payload === 'ADD' ? current + 1 : current - 1
			)
		}
	}

	React.useEffect(
		() => setUrl(generateEndpointUrl(API_URL, request, queryParams)),
		[request]
	)

	React.useEffect(() => {
		const abortController = new AbortController()
	if (!sleeper || force) {
  dispatchSource('ADD', silent)
  setFetching(true)

			const cacheItem = sessionStorage.getItem(CACHE_KEY)
			if (cacheItem) {
				setData(JSON.parse(cacheItem))
				setFetching(false)
				setForce(false)
				setSleeping(true)
				dispatchSource('REMOVE', silent)
			} else {
				fetchData<T>(
					method,
					url,
					file,
					abortController.signal,
					{
						'Id-Sessione': dataState.token?.sessione ?? undefined,
						'Id-Sessione-Mobile': mobileToken ?? undefined,
					},
					body || {},
					passwordHeader,
					valueHeader,
					hmacKey
				)
					.then((res) => {
						setData(res ?? null)

						if (shouldCache) {
							sessionStorage.setItem(CACHE_KEY, JSON.stringify(res))
						}
					})

					.catch((err) => {
						console.log('ERRORE CATTURATO:', err)
						const e = new Error(err)
						showBoundary(e)
					})

					.finally(() => {
						if (!abortController.signal.aborted) {
							setFetching(false)
							setForce(false)
							setSleeping(true)
							dispatchSource('REMOVE', silent)
						}
					})
			}
		}
		return () => {
			if (fetching) console.log('aborting', url)
		}
	}, [body, method, url, force, request])

	return [data, trigger, fetching]
}

export { useApi, fetchData }
export type { APIRequest }
